/** ADMINISTRATION — la vue d'ensemble de Servae.
 *  Tout ce qui se passe : les pros, leur activité, l'argent, l'usage des fonctionnalités.
 *  Et surtout : voir l'application avec les yeux d'un pro, ou avec ceux d'une de ses clientes. */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ExternalLink, Eye, LogOut, RefreshCw, Search, User, Users,
} from "lucide-react";
import { api, apercu, messageErreur } from "../api";
import { Btn, Erreur, Micro, Squelette } from "../components/Bits";
import { useApp } from "../App";

const euro = (n) =>
  `${Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;

const jour = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : "—";

const FONCTION_LABEL = {
  voix: "La Voix",
  journal: "Journal du soir",
  story: "Kit Instagram",
  contenu: "Contenu semaine",
  marque: "Brand Auto",
  assistant: "Assistant DM",
};

function Tuile({ valeur, libelle, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-4"
      style={{
        background: accent ? "var(--accent-soft)" : "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
      }}
    >
      <p className="num text-[26px] font-semibold leading-none"
        style={{ color: accent ? "var(--accent)" : "var(--text)" }}>
        {valeur}
      </p>
      <p className="text-[12.5px] mt-1.5" style={{ color: "var(--text-subtle)" }}>
        {libelle}
      </p>
    </div>
  );
}

function Bloc({ titre, children }) {
  return (
    <section className="mt-8">
      <Micro className="mb-2.5 block">{titre}</Micro>
      {children}
    </section>
  );
}

function Ligne({ p, choisi, surChoix }) {
  return (
    <button
      onClick={surChoix}
      data-testid={`admin-pro-${p.id}`}
      className="w-full text-left px-4 py-3 flex items-center gap-4"
      style={{
        background: choisi ? "var(--accent-soft)" : "var(--surface)",
        borderBottom: "1px solid var(--border)",
        transition: "background-color .15s ease",
      }}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold truncate">
          {p.activite || p.nom || p.email}
        </span>
        <span className="block text-[12.5px] truncate" style={{ color: "var(--text-subtle)" }}>
          {p.email} · {p.metier}
          {p.ville ? ` · ${p.ville}` : ""}
        </span>
      </span>
      <span className="hidden sm:block text-[12.5px] w-20 shrink-0" style={{ color: "var(--text-subtle)" }}>
        {p.installe ? "Installé" : "En cours"}
      </span>
      <span className="num text-[13px] w-14 shrink-0 text-right">{p.clientes}</span>
      <span className="num text-[13px] w-14 shrink-0 text-right">{p.rdv}</span>
      <span className="num text-[13px] w-20 shrink-0 text-right font-semibold">{euro(p.encaisse)}</span>
      <span className="hidden md:block num text-[12px] w-16 shrink-0 text-right"
        style={{ color: "var(--text-subtle)" }}>
        {jour(p.cree_le)}
      </span>
    </button>
  );
}

function Detail({ id, surApercu }) {
  const [d, setD] = useState(null);
  const [clientes, setClientes] = useState(null);

  useEffect(() => {
    setD(null);
    setClientes(null);
    api.get(`/admin/pros/${id}`).then(({ data }) => setD(data)).catch(() => setD(false));
  }, [id]);

  const voirClientes = async () => {
    try {
      const { data } = await api.get(`/admin/pros/${id}/clientes`);
      setClientes(data.clientes);
      if (!data.clientes.length) toast.message("Ce pro n'a encore aucune cliente.");
    } catch (e) {
      toast.error(messageErreur(e));
    }
  };

  if (d === false) return <p className="text-[14px]">Fiche introuvable.</p>;
  if (!d) return <Squelette lignes={3} />;

  const p = d.pro;
  return (
    <div data-testid="admin-detail">
      <p className="text-[20px] font-semibold">{p.activite || p.nom || p.email}</p>
      <p className="text-[13px]" style={{ color: "var(--text-subtle)" }}>
        {p.email} · {d.metier_label}
        {p.ville ? ` · ${p.ville}` : ""} · inscrit le {jour(p.created_at)}
      </p>

      <div className="flex flex-wrap gap-2 mt-4">
        <Btn icone={Eye} onClick={() => surApercu(id)} data-testid="admin-voir-ecrans">
          Voir ses écrans
        </Btn>
        {p.slug && (
          <Btn
            variante="fantome"
            icone={ExternalLink}
            onClick={() => window.open(`/r/${p.slug}`, "_blank")}
            data-testid="admin-voir-vitrine"
          >
            Sa page publique
          </Btn>
        )}
        <Btn variante="nu" icone={Users} onClick={voirClientes} data-testid="admin-voir-clientes">
          Écrans de ses clientes
        </Btn>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-5">
        <Tuile valeur={d.compteurs.clientes} libelle="Clientes" />
        <Tuile valeur={d.compteurs.rdv} libelle="Rendez-vous" />
        <Tuile valeur={euro(d.compteurs.encaisse)} libelle="Encaissé" accent />
        <Tuile valeur={d.compteurs.absents} libelle="Absences" />
        <Tuile valeur={d.compteurs.prestations} libelle="Prestations" />
      </div>

      {clientes && clientes.length > 0 && (
        <Bloc titre="Espaces clientes — tels qu'elles les reçoivent">
          <div className="space-y-1.5">
            {clientes.map((c) => (
              <button
                key={c.id}
                onClick={() => window.open(`/moi/${c.jeton}`, "_blank")}
                data-testid={`admin-espace-${c.id}`}
                className="w-full flex items-center gap-2.5 text-left px-3.5 py-2.5"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <User size={15} strokeWidth={1.7} style={{ color: "var(--accent)" }} />
                <span className="text-[14px] flex-1 truncate">{c.nom}</span>
                <ExternalLink size={14} style={{ color: "var(--text-subtle)" }} />
              </button>
            ))}
          </div>
        </Bloc>
      )}

      <Bloc titre="Fonctionnalités utilisées">
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(FONCTION_LABEL).map((f) => {
            const on = d.usage[f];
            return (
              <span
                key={f}
                className="text-[12.5px] px-2.5 py-1"
                style={{
                  borderRadius: "var(--radius-full)",
                  background: on ? "var(--accent-soft)" : "var(--surface-sunken)",
                  color: on ? "var(--accent)" : "var(--text-subtle)",
                  fontWeight: on ? 600 : 400,
                }}
              >
                {FONCTION_LABEL[f]}
                {on ? ` · ${on.n}` : ""}
              </span>
            );
          })}
        </div>
      </Bloc>

      {d.prestations.length > 0 && (
        <Bloc titre="Ses prestations">
          <div className="space-y-1">
            {d.prestations.map((s) => (
              <p key={s.nom} className="text-[14px]">
                {s.nom}
                <span className="num" style={{ color: "var(--text-subtle)" }}>
                  {" "}· {s.duree_min} min · {s.prix} €
                </span>
                {!s.actif && (
                  <span className="text-[12px]" style={{ color: "var(--text-subtle)" }}>
                    {" "}(désactivée)
                  </span>
                )}
              </p>
            ))}
          </div>
        </Bloc>
      )}

      {d.derniers_rdv.length > 0 && (
        <Bloc titre="Ses derniers rendez-vous">
          <div className="space-y-1">
            {d.derniers_rdv.map((r) => (
              <p key={r.id} className="text-[13.5px]">
                <span className="num" style={{ color: "var(--text-subtle)" }}>
                  {jour(r.quand)}
                </span>{" "}
                {r.cliente} — {r.prestation}
                <span className="num" style={{ color: "var(--text-subtle)" }}>
                  {" "}· {r.prix} € {r.paye ? "payé" : r.statut}
                </span>
              </p>
            ))}
          </div>
        </Bloc>
      )}
    </div>
  );
}

export default function Admin() {
  const { deconnexion } = useApp();
  const [kpi, setKpi] = useState(null);
  const [pros, setPros] = useState([]);
  const [signaux, setSignaux] = useState([]);
  const [q, setQ] = useState("");
  const [choisi, setChoisi] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [charge, setCharge] = useState(true);

  const charger = useCallback(async (recherche = "") => {
    setCharge(true);
    setErreur(null);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/apercu"),
        api.get("/admin/pros", { params: recherche ? { q: recherche } : {} }),
        api.get("/admin/signaux"),
      ]);
      setKpi(a.data);
      setPros(b.data.pros);
      setSignaux(c.data.signaux);
    } catch (e) {
      setErreur(messageErreur(e, "Impossible de charger l'administration."));
    } finally {
      setCharge(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  const ouvrirApercu = async (id) => {
    try {
      const { data } = await api.post(`/admin/pros/${id}/apercu`);
      apercu.entrer(data.token, data.pro);
      window.location.href = "/journee";
    } catch (e) {
      toast.error(messageErreur(e));
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className="mx-auto max-w-[1100px] px-5 py-8" data-testid="page-admin">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Micro>Administration</Micro>
            <h1 className="display text-[30px] sm:text-[38px] leading-tight">Servae, en entier</h1>
          </div>
          <div className="flex gap-2">
            <Btn variante="fantome" icone={RefreshCw} onClick={() => charger(q)} data-testid="admin-recharger">
              Actualiser
            </Btn>
            <Btn variante="nu" icone={LogOut} onClick={deconnexion} data-testid="admin-deconnexion">
              Quitter
            </Btn>
          </div>
        </div>

        {erreur ? (
          <div className="mt-8">
            <Erreur message={erreur} surReessai={() => charger(q)} />
          </div>
        ) : charge && !kpi ? (
          <div className="mt-8">
            <Squelette lignes={4} />
          </div>
        ) : (
          <>
            <Bloc titre="Les pros">
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                <Tuile valeur={kpi.pros.total} libelle="Inscrits" accent testid="admin-kpi-pros" />
                <Tuile valeur={kpi.pros.nouveaux_7j} libelle="Cette semaine" />
                <Tuile valeur={kpi.pros.installes} libelle="Installés" />
                <Tuile valeur={kpi.pros.actifs_7j} libelle="Actifs 7 j" />
                <Tuile valeur={kpi.pros.en_essai} libelle="En essai" />
                <Tuile valeur={kpi.pros.payants} libelle="Abonnés" />
                <Tuile valeur={kpi.pros.avec_marque} libelle="Marque perso" />
              </div>
            </Bloc>

            <Bloc titre="Activité et argent">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <Tuile valeur={kpi.activite.rdv_total} libelle="Rendez-vous" />
                <Tuile valeur={kpi.activite.rdv_30j} libelle="Sur 30 jours" />
                <Tuile valeur={kpi.activite.clientes} libelle="Clientes" />
                <Tuile valeur={`${kpi.activite.taux_absence} %`} libelle="Absences" />
                <Tuile valeur={euro(kpi.argent.encaisse_total)} libelle="Encaissé via Servae" accent />
                <Tuile valeur={euro(kpi.argent.abonnement_mensuel)} libelle="Abonnements / mois" accent />
              </div>
            </Bloc>

            <Bloc titre={`À regarder maintenant (${signaux.length})`}>
              {signaux.length ? (
                <div className="space-y-1.5" data-testid="admin-signaux">
                  {signaux.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setChoisi(s.id)}
                      data-testid={`admin-signal-${s.id}`}
                      className="w-full flex items-center gap-3 text-left px-3.5 py-2.5"
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderLeft: `3px solid ${
                          s.urgence === 0 ? "var(--accent)" : s.urgence === 1 ? "var(--danger)" : "var(--border)"
                        }`,
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      <span className="text-[14px] font-semibold flex-1 truncate">{s.nom}</span>
                      <span className="text-[13px] shrink-0" style={{ color: "var(--text-muted)" }}>
                        {s.raison}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[14px]" style={{ color: "var(--text-subtle)" }}>
                  Rien à signaler : personne n’est bloqué dans son installation, personne n’a
                  décroché.
                </p>
              )}
            </Bloc>

            <Bloc titre="Ce qui sert vraiment">
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(kpi.usage).map(([f, u]) => (
                  <span
                    key={f}
                    className="text-[12.5px] px-2.5 py-1"
                    style={{
                      borderRadius: "var(--radius-full)",
                      background: u.appels ? "var(--accent-soft)" : "var(--surface-sunken)",
                      color: u.appels ? "var(--accent)" : "var(--text-subtle)",
                    }}
                  >
                    {FONCTION_LABEL[f] || f} · <span className="num">{u.pros}</span> pros ·{" "}
                    <span className="num">{u.appels}</span> usages
                  </span>
                ))}
              </div>
            </Bloc>

            <Bloc titre={`Les professionnels (${pros.length})`}>
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1 max-w-[360px]">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    style={{ color: "var(--text-subtle)" }}
                  />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && charger(q)}
                    placeholder="Nom, e-mail, ville…"
                    data-testid="admin-recherche"
                    className="w-full h-10 pl-9 pr-3 text-[14px] outline-none focus:border-[var(--accent)]"
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                    }}
                  />
                </div>
                <Btn variante="fantome" onClick={() => charger(q)} data-testid="admin-chercher">
                  Chercher
                </Btn>
              </div>

              <div
                className="overflow-hidden"
                style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)" }}
              >
                <div
                  className="flex items-center gap-4 px-4 py-2 text-[11px] tracking-[.12em] uppercase"
                  style={{ background: "var(--surface-sunken)", color: "var(--text-subtle)" }}
                >
                  <span className="flex-1">Professionnel</span>
                  <span className="hidden sm:block w-20 shrink-0">État</span>
                  <span className="w-14 shrink-0 text-right">Clientes</span>
                  <span className="w-14 shrink-0 text-right">RDV</span>
                  <span className="w-20 shrink-0 text-right">Encaissé</span>
                  <span className="hidden md:block w-16 shrink-0 text-right">Inscrit</span>
                </div>
                {pros.map((p) => (
                  <Ligne
                    key={p.id}
                    p={p}
                    choisi={choisi === p.id}
                    surChoix={() => setChoisi(choisi === p.id ? null : p.id)}
                  />
                ))}
                {!pros.length && (
                  <p className="px-4 py-6 text-[14px]" style={{ color: "var(--text-subtle)" }}>
                    Aucun professionnel ne correspond.
                  </p>
                )}
              </div>
            </Bloc>

            {choisi && (
              <section
                className="mt-6 p-5"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-lg)",
                }}
              >
                <Detail id={choisi} surApercu={ouvrirApercu} />
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
